import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ConstitutionMapperService } from 'src/app/shared/service/constitution/constitution-detail/constitution-mapper.service';
import { KushkiPaymentsMapperService } from '../services/kushki.mapper.service';
import { AssociatedCostsCatalogMapperService } from '../services/associated-costs-catalog.mapper.service';
import { DetailConstitution } from 'src/app/shared/models/constitution/client-constitucion.model';
import { KushkiPayment } from '../../domain/models/kushki.model';
import { CatalogOption } from '../../domain/models/associated-costs-catalog.model';
import { ManageInfoClientRepository } from '../../domain/repository/manage-info-client.repository';
import { ConstitutionApiService } from 'src/app/core/service/apis/constitutions/constitution-api.service';
import { toApplicationField } from 'src/app/shared/constants/application-field.constants';
import { toPartnerField } from '../constants/partner-field.constants';
import { toCostField } from 'src/app/shared/constants/cost-field.constants';

@Injectable()
export class HttpManageInfoClientRepository implements ManageInfoClientRepository {
  private readonly typeEnvironmentApim = environment.Apim;

  constructor(
    private readonly api: ConstitutionApiService,
    private readonly http: HttpClient,
    private readonly mapper: ConstitutionMapperService,
    private readonly kushkiMapper: KushkiPaymentsMapperService,
    private readonly catalogMapper: AssociatedCostsCatalogMapperService,
  ) { }

  getConstitutionDetail(recordId: string): Observable<DetailConstitution | null> {
    return this.api.getConstitutionDetail(recordId).pipe(
      map((res: any) => (res?.recordId ? this.mapper.mapToConstitutionDetailStandard(res) : null)),
    );
  }

  updateStringField(recordId: string, field: string, value: string): Observable<void> {
    const costField = toCostField(field);
    if (costField) {
      return this.api.updateCostField(recordId, costField, value);
    }

    const applicationField = toApplicationField(field);

    if (!applicationField) {
      return throwError(() => new Error(`Campo no soportado por update/field/application: ${field}`));
    }

    return this.api.updateApplicationField(recordId, applicationField, value).pipe(
      map(() => undefined),
    );
  }

  updatePartnerField(partnerRecordId: string, field: string, value: string): Observable<void> {
    const partnerField = toPartnerField(field);

    if (!partnerField) {
      return throwError(() => new Error(`Campo no soportado por update/field/partner: ${field}`));
    }

    return this.http.patch<void>(
      `${this.typeEnvironmentApim.migration_host}/` +
      `${this.typeEnvironmentApim.migration_paths.api}/` +
      `${this.typeEnvironmentApim.migration_paths.updateFieldPartner}/${partnerRecordId}`,
      { fieldName: partnerField, value }
    );
  }

  updateBusinessNameReservation(
    recordId: string,
    payload: { nombreReservado: string; reservationDate: string }
  ): Observable<void> {
    return this.api.updateBusinessNameReservation(recordId, payload);
  }

  generateAutomatedMinute(recordId: string): Observable<void> {
    return this.http.get<any>(
      `${this.typeEnvironmentApim.migration_host}/` +
      `${this.typeEnvironmentApim.migration_paths.api}/` +
      `${this.typeEnvironmentApim.migration_paths.document}/` +
      `${this.typeEnvironmentApim.migration_paths.documentGenerateAutomatedMinute}/${recordId}`
    ).pipe(
      map(() => undefined),
    );
  }

  getKushkiPayments(constitutionRecordId: string): Observable<KushkiPayment[]> {
    return this.http.get<any>(
      `${this.typeEnvironmentApim.migration_host}/` +
      `${this.typeEnvironmentApim.migration_paths.api}/` +
      `${this.typeEnvironmentApim.migration_paths.auditKushki}/${constitutionRecordId}`
    ).pipe(
      map((res: any) => this.kushkiMapper.mapKushkiResponse(res ?? [])),
    );
  }

  getAssociatedCostsCatalog(associatedCosts: string, typeFlow: string): Observable<CatalogOption[]> {
    return this.http.get<any[]>(
      `${this.typeEnvironmentApim.migration_host}/` +
      `${this.typeEnvironmentApim.migration_paths.api}/` +
      `${this.typeEnvironmentApim.migration_paths.catalogsAssociatedCosts}`,
      { params: { associatedCosts, typeFlow } }
    ).pipe(
      map((res) => this.catalogMapper.mapCatalogResponse(res ?? [])),
    );
  }
}
